import { MIME_TYPES } from "@mantine/dropzone";
import { FileTypeEnum } from "../graphql/graphql";
export const getFileTypeFromMimeType = (mimeType) => {
    if (!mimeType)
        return "OTHER_UPLOAD";
    const map = {
        "text/csv": "CSV_UPLOAD",
        "application/pdf": "PDF_UPLOAD",
        // Document types
        "application/msword": "DOCUMENT_UPLOAD",
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "DOCUMENT_UPLOAD",
        "application/vnd.ms-excel": "DOCUMENT_UPLOAD",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "DOCUMENT_UPLOAD",
        "application/vnd.ms-powerpoint": "DOCUMENT_UPLOAD",
        "application/vnd.openxmlformats-officedocument.presentationml.presentation": "DOCUMENT_UPLOAD",
        // Image types
        "image/png": "IMAGE_UPLOAD",
        "image/gif": "IMAGE_UPLOAD",
        "image/jpeg": "IMAGE_UPLOAD",
        "image/svg+xml": "IMAGE_UPLOAD",
        "image/webp": "IMAGE_UPLOAD",
        "image/heic": "IMAGE_UPLOAD",
        "image/heif": "IMAGE_UPLOAD",
        // Video
        "video/mp4": "VIDEO_UPLOAD",
        // Zip
        "application/zip": "ZIP_UPLOAD",
        "application/vnd.rar": "ZIP_UPLOAD"
    };
    return map[mimeType] ?? "OTHER_UPLOAD";
};
// Format bytes to a more readable format
export const formatBytes = (bytes) => {
    if (!bytes)
        return "0 B";
    const k = 1024;
    const sizes = ["B", "KB", "MB", "GB", "TB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + " " + sizes[i];
};
export const generateVideoThumbnail = (file) => {
    return new Promise((resolve, reject) => {
        const video = document.createElement("video");
        video.preload = "metadata";
        video.muted = true;
        video.playsInline = true;
        video.src = URL.createObjectURL(file);
        video.onloadedmetadata = () => {
            video.currentTime = 1; // Seek to 1 second to get a good frame
        };
        video.onseeked = () => {
            const canvas = document.createElement("canvas");
            canvas.width = video.videoWidth;
            canvas.height = video.videoHeight;
            const ctx = canvas.getContext("2d");
            if (ctx) {
                ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
                const thumbnail = canvas.toDataURL("image/jpeg");
                URL.revokeObjectURL(video.src);
                resolve(thumbnail);
            }
            else {
                reject("Failed to get canvas context");
            }
        };
        video.onerror = () => {
            URL.revokeObjectURL(video.src);
            reject("Failed to load video");
        };
    });
};
export const getFileIconColor = (fileType) => {
    const iconColor = fileType === FileTypeEnum.PdfUpload
        ? "var(--mantine-color-orange-5)"
        : fileType === FileTypeEnum.ImageUpload
            ? "var(--mantine-color-blue-5)"
            : fileType === FileTypeEnum.CsvUpload
                ? "var(--mantine-color-violet-5)"
                : fileType === FileTypeEnum.DocumentUpload
                    ? "var(--mantine-color-gray-5)"
                    : fileType === FileTypeEnum.VideoUpload
                        ? "var(--mantine-color-green-5)"
                        : fileType === FileTypeEnum.ZipUpload
                            ? "var(--mantine-color-red-5)"
                            : "var(--mantine-color-pink-5)";
    return iconColor;
};
export const getFileMimeType = (fileName, mimeType) => {
    // Attempt to derive the file type from the file name
    const nameParts = fileName.split("-");
    const typeFromName = nameParts[nameParts.length - 1];
    // Find the matching MIME type and get its key (extension)
    const matchingType = Object.entries(MIME_TYPES).find(([_, mimeType]) => mimeType.includes(typeFromName || ""))?.[0];
    if (typeFromName && matchingType) {
        return matchingType;
    }
    // If that doesn't work, try to use the mime type
    const mimeParts = mimeType.split("/");
    const typeFromMime = mimeParts[mimeParts.length - 1];
    const matchingTypeFromMime = Object.entries(MIME_TYPES).find(([_, mimeType]) => mimeType.includes(typeFromMime || ""))?.[0];
    if (typeFromMime && matchingTypeFromMime) {
        return matchingTypeFromMime;
    }
    return "OTHER_UPLOAD";
};
// Helper function to convert data to CSV and download
export const downloadCSV = (data, columns, filename) => {
    // Validate inputs
    if (!Array.isArray(data)) {
        throw new Error("Data must be an array");
    }
    if (!data.length) {
        throw new Error("Data array is empty");
    }
    // Create CSV content
    const headers = columns.map((col) => col.header);
    const csvContent = [
        headers.join(","),
        ...data.map((row) => {
            if (!row || typeof row !== "object") {
                return columns.map(() => '""').join(",");
            }
            return columns
                .map((col) => {
                const value = row[col.key];
                // Handle null/undefined values
                if (value == null)
                    return '""';
                // Convert to string and handle circular references
                let stringValue;
                try {
                    stringValue = String(value);
                }
                catch (error) {
                    console.warn("Failed to convert value to string:", value, error);
                    stringValue = "";
                }
                // Escape quotes and wrap in quotes to handle commas and special characters
                const escapedValue = stringValue.replace(/"/g, '""');
                return `"${escapedValue}"`;
            })
                .join(",");
        })
    ].join("\n");
    // Create blob and download
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    if (link.download !== undefined) {
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", filename);
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    }
};
